import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Row, Space, Upload } from 'antd';
import type { UploadProps } from 'antd';
import previewImageIcon from 'assets/icons/preveiw-image.png';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import configurationService from 'common/services/configuration-service/configuration.service';
import { IDefaultConfigurationUpdateRequest } from 'common/services/configuration-service/configuration.service.types';
import FileService from 'common/services/file-service/file.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

export const MAX_FILE_SIZE_BYTES = 1000 * 1024;

const readImageFile = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(file);
  });

const CommunitySettingsConfiguration = observer(() => {
  const [form] = Form.useForm();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [selectedLogo, setSelectedLogo] = useState<Blob | null>(null);

  const communityLogoUrl = configurationStore.configuration?.default?.communityLogoUrl ?? '';

  useEffect(() => {
    setLogoPreviewUrl(communityLogoUrl);
    setSelectedLogo(null);
  }, [communityLogoUrl]);

  const handleSubmit = async () => {
    let logoUrl = logoPreviewUrl;

    if (selectedLogo) {
      const uploadedLogo = await FileService.uploadPhoto(selectedLogo);
      logoUrl = uploadedLogo.absoluteUri;
    }

    const model: IDefaultConfigurationUpdateRequest = {
      default: {
        communityLogoUrl: logoUrl,
      },
    };

    const updatedConfiguration = await configurationService.updateSystemConfiguration(model);

    configurationStore.setConfiguration(updatedConfiguration);
    setSelectedLogo(null);
    setLogoPreviewUrl(logoUrl);

    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  const uploadProps: UploadProps = {
    beforeUpload: async (file) => {
      if (file.size >= MAX_FILE_SIZE_BYTES) {
        notification.error({
          message: 'The image size must be less than 200 KB!',
        });

        return Upload.LIST_IGNORE;
      }

      try {
        const previewUrl = await readImageFile(file);

        setSelectedLogo(file);
        setLogoPreviewUrl(previewUrl);
      } catch {
        notification.error({
          message: 'Unable to preview the selected image.',
        });
      }

      return false;
    },
    accept: '.png,.svg',
    maxCount: 1,
    showUploadList: false,
  };

  const handleRemoveLogo = () => {
    setSelectedLogo(null);
    setLogoPreviewUrl('');
  };

  const hasLogo = Boolean(logoPreviewUrl);

  return (
    <FormWrapper form={form} onFinish={handleSubmit} layout="vertical" style={{ height: '100%' }}>
      <Card className="eiq-card eiq-card-border eiq-configuration community-settings-configuration" style={{ height: '100%' }}>
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">Community Settings</h1>
          </Col>
        </Row>

        <Row>
          <Col xs={24} sm={12}>
            <Space className="full-width" direction="vertical">
              <Form.Item label="Community Logo">
                <div className="logoControls">
                  <Upload {...uploadProps} className="logoUpload">
                    <img className="logoPreview" src={logoPreviewUrl || previewImageIcon} alt="Community logo" />

                    {!hasLogo && (
                      <Button className="secondary-btn logoActionButton" icon={<PlusOutlined />} size="middle">
                        Add
                      </Button>
                    )}
                  </Upload>

                  {hasLogo && (
                    <Button className="secondary-btn logoActionButton" icon={<Trash />} size="middle" onClick={handleRemoveLogo}>
                      Remove
                    </Button>
                  )}
                </div>
              </Form.Item>
            </Space>

            <Button block htmlType="submit" type="primary" icon={<SaveOutlined />}>
              Save
            </Button>
          </Col>
        </Row>
      </Card>
    </FormWrapper>
  );
});

export default CommunitySettingsConfiguration;
